<script setup lang="ts">
import OperationsTableHeader from './OperationsTableHeader.vue';
import OperationsTableRow from './OperationsTableRow.vue';
import OperationsSubActionRow from './OperationsSubActionRow.vue';
import OperationsTableFooter from './OperationsTableFooter.vue';

interface FlattenedItem {
  id: string;
  type: 'assignment' | 'sub-action';
  isRecipe?: boolean;
  data: any;
}

interface HoursByType {
  [key: string]: number;
  total: number;
}

const props = defineProps<{
  items: FlattenedItem[];
  loading: boolean;
  hoursByType: HoursByType;
  getActionName: (item: FlattenedItem) => string;
  isActionLoading: (actionId?: string) => boolean;
  getLaborHours: (item: FlattenedItem) => number;
  getLaborAssignees: (item: FlattenedItem) => Array<{ id: string; name: string }>;
  getFinancialData: (item: FlattenedItem, type: 'charged' | 'actual' | 'quote') => string;
  getFinancialTotal: (type: 'charged' | 'actual' | 'quote') => string;
}>();

const emit = defineEmits<{
  (e: 'edit-assignment', assignment: any): void;
  (e: 'delete-assignment', assignment: any, event: Event): void;
  (e: 'view-financials', assignment: any): void;
}>();

function handleEditAssignment(assignment: any) {
  emit('edit-assignment', assignment);
}

function handleDeleteAssignment(assignment: any, event: Event) {
  emit('delete-assignment', assignment, event);
}

</script>


<template>
  <div class="border border-gray-200 dark:border-gray-700 rounded overflow-hidden">
    <!-- Table Header -->
    <OperationsTableHeader />

    <!-- Table Body: Loading State -->
    <div v-if="loading" class="p-4 text-center">
      <i class="pi pi-spin pi-spinner mr-2 text-blue-500"></i>
      <span>Loading operations...</span>
    </div>

    <!-- Table Body: Content -->
    <div v-else-if="items.length > 0" class="max-h-[60vh] overflow-y-auto">
      <template v-for="(item, index) in items" :key="item.id">
        <!-- Assignment Row -->
        <OperationsTableRow
          v-if="item.type === 'assignment'"
          :assignment="item.data"
          :index="index"
          :is-recipe="!!item.isRecipe"
          :action-name="getActionName(item)"
          :loading="isActionLoading(item.data.action?.id)"
          :labor-hours="getLaborHours(item)"
          :mechanics="getLaborAssignees(item)"
          :charged-amount="getFinancialData(item, 'charged')"
          :actual-amount="getFinancialData(item, 'actual')"
          :quote-amount="getFinancialData(item, 'quote')"
          @edit="handleEditAssignment"
          @delete="handleDeleteAssignment"
        />

        <!-- Sub-Action Row -->
        <OperationsSubActionRow
          v-else-if="item.type === 'sub-action'"
          :sub-action="item.data"
        />
      </template>

      <!-- Table Footer with Totals -->
      <OperationsTableFooter
        :total-hours="hoursByType.total"
        :charged-total="getFinancialTotal('charged')"
        :actual-total="getFinancialTotal('actual')"
        :quote-total="getFinancialTotal('quote')"
      />
    </div>

    <!-- Empty State -->
    <div v-else class="text-center p-6 bg-gray-50 dark:bg-gray-800/50">
      <i class="pi pi-wrench text-4xl text-gray-400 dark:text-gray-500 mb-3"></i>
      <p class="text-gray-700 dark:text-gray-300">No assignments found for this ticket</p>
      <p class="text-gray-500 text-sm mt-2">Add an assignment to start tracking work</p>
    </div>
  </div>
</template>
